import { useState, useMemo } from 'react';
import { UseFilteredRolesParams } from './types';

const parseAllowedRoles = (allowedRoles?: string) => {
  if (!allowedRoles) return null;

  try {
    const parsed = JSON.parse(allowedRoles);
    if (Array.isArray(parsed)) {
      return new Set(parsed.filter((role): role is string => typeof role === 'string'));
    }
  } catch {
    return new Set(allowedRoles.split(',').map((role) => role.trim()).filter(Boolean));
  }

  return null;
};

export const useFilteredRoles = ({ roles, allowedRoles }: UseFilteredRolesParams) => {
  const [searchText, setSearchText] = useState('');
  const allowedRoleSet = useMemo(() => parseAllowedRoles(allowedRoles), [allowedRoles]);

  const filteredRoles = useMemo(() => {
    const permitted = (roles ?? []).filter(
      (role) => !allowedRoleSet || allowedRoleSet.has(role.category),
    );
    const ordered = [...permitted].sort((a, b) => (
      a.roleLabel.localeCompare(b.roleLabel, 'es', { sensitivity: 'base' })
    ));

    const query = searchText.trim().toLowerCase();
    if (!query) return ordered;

    return ordered.filter(
      (r) => r.roleLabel.toLowerCase().includes(query)
      || r.category.toLowerCase().includes(query),
    );
  }, [roles, allowedRoleSet, searchText]);

  return { searchText, setSearchText, filteredRoles };
};

export default useFilteredRoles;
